import { IconButton, Stack } from '@mui/material';
import { useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { LassieWorkstation } from '@thive/fddm-types';

import { PlusIcon } from '@/assets/icons';
import { WorkspaceMode } from '@/modules/common/types/general';
import { Accordion } from '@/modules/common/components/Accordion';
import { useAccordionExpanded } from '@/modules/userPreferences/hooks';

import { WorkstationConfig } from './WorkstationConfig';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { UserPreferenceName } from '@/modules/userPreferences';

type Props = {
  configs: LassieWorkstation[];
  updateConfigs: (value: LassieWorkstation[]) => void;
  onAddClick: () => void;
};

const WorkstationConfigsComponent = ({ configs, updateConfigs, onAddClick }: Props) => {
  const { t } = useTranslation('interface');
  const mode = useAppStore((state) => state.mode)
  const { expanded: open = true, setExpanded: setOpen } = useAccordionExpanded(
    UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_WORKSTATION_CONFIGS,
  );

  const onAddWorkstationClick = useCallback(
    (e) => {
      e.stopPropagation();
      setOpen(true);

      onAddClick();
    },
    [onAddClick, setOpen],
  );

  const updatePosition = useCallback(
    (workstationConfigIndex: number, value: number) => {
      const updatedConfigs = configs.map((item, i) =>
        i === workstationConfigIndex
          ? {
              ...item,
              positionFromBottom: value,
            }
          : item,
      );

      updateConfigs(updatedConfigs);
    },
    [configs, updateConfigs],
  );

  const removeWorkstation = useCallback(
    (workstationConfigIndex: number) => {
      const updatedConfigs = configs.filter((item, i) =>
        i === workstationConfigIndex ? false : item,
      );

      updateConfigs(updatedConfigs);
    },
    [configs, updateConfigs],
  );

  return (
    <Accordion
      title={t('properties.template.workstation.header', { count: configs.length })}
      expanded={open}
      onChange={(expanded: boolean) => setOpen(expanded)}
      afterIcon={
        <IconButton
          onClick={onAddWorkstationClick}
          size='small'
          sx={{ p: 0, height: 'fit-content', marginLeft: 'auto', marginRight: 1 }}
          disabled={mode !== WorkspaceMode.EDITABLE}
        >
          <PlusIcon />
        </IconButton>
      }
    >
      <Stack gap={1}>
        {configs.map((item, i) => (
          <WorkstationConfig
            // NOTE: keep component pure so we can use index as key
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            index={i}
            name={`Workstation ${i + 1}`}
            type={item.type}
            positionFromBottom={item.positionFromBottom}
            disableAllInteraction={mode !== WorkspaceMode.EDITABLE}
            updatePosition={updatePosition}
            removeWorkstation={removeWorkstation}
          />
        ))}
      </Stack>
    </Accordion>
  );
};

export const WorkstationConfigs = memo(WorkstationConfigsComponent);
